import { Clock, FileText, Image as ImageIcon, Video, FileCode, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ContentItem } from '@/hooks/useCompanyContent'
import { Link } from 'react-router-dom'

interface RecentContentProps {
  content: ContentItem[]
  maxItems?: number
  emptyMessage?: string
  showViewAll?: boolean
  viewAllLink?: string
}

export function RecentContent({
  content,
  maxItems = 5,
  emptyMessage = 'No recent content',
  showViewAll = true,
  viewAllLink = '/content'
}: RecentContentProps) {
  // Sort content by creation date (newest first) and limit
  const recentContent = [...content]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, maxItems)

  const getTypeIcon = (type: string) => {
    const iconClassName = "w-4 h-4"
    switch (type) {
      case 'presentation':
        return <FileText className={iconClassName} />
      case 'pdf':
        return <FileText className={iconClassName} />
      case 'image':
        return <ImageIcon className={iconClassName} />
      case 'video':
        return <Video className={iconClassName} />
      case 'markdown':
        return <FileCode className={iconClassName} />
      default:
        return <FileText className={iconClassName} />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'presentation':
        return 'text-blue-600 dark:text-blue-400'
      case 'pdf':
        return 'text-red-600 dark:text-red-400'
      case 'image':
        return 'text-green-600 dark:text-green-400'
      case 'video':
        return 'text-purple-600 dark:text-purple-400'
      case 'markdown':
        return 'text-orange-600 dark:text-orange-400'
      default:
        return 'text-muted-foreground'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return 'Today'
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return `${diffDays} days ago`
    } else {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    }
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

    if (diffHours < 1) {
      return 'Just now'
    } else if (diffHours < 24) {
      return `${diffHours}h ago`
    } else if (diffDays < 7) {
      return `${diffDays}d ago`
    } else {
      return formatDate(dateString)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Recent Content</h3>
        </div>
        {showViewAll && content.length > maxItems && (
          <Link to={viewAllLink}>
            <Button variant="ghost" size="sm" className="text-xs">
              View All
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        )}
      </div>

      {/* Recent Content List */}
      {recentContent.length > 0 ? (
        <div className="space-y-2">
          {recentContent.map((item) => (
            <Link
              key={item.id}
              to={`/content/${item.id}`}
              className="block"
            >
              <div className="flex items-start gap-3 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors">
                {/* Thumbnail or Type Icon */}
                {item.thumbnailUrl ? (
                  <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted mt-0.5">
                    <img
                      src={item.thumbnailUrl}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className={`p-2 rounded-lg bg-muted ${getTypeColor(item.type)} mt-0.5`}>
                    {getTypeIcon(item.type)}
                  </div>
                )}

                {/* Content Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-foreground mb-0.5 line-clamp-1">
                    {item.title}
                  </h4>
                  {item.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mb-1">
                      {item.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{formatRelativeTime(item.createdAt)}</span>
                    <span>•</span>
                    <span className="capitalize">{item.type}</span>
                  </div>
                </div>

                {/* External Link Icon */}
                <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
              </div>
            </Link>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="p-6 bg-muted/30 border border-border rounded-lg text-center">
          <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </div>
      )}
    </div>
  )
}
